import React from 'react';
import { Label, Pie, PieChart, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { SectionCard } from './SectionCard';
import { ChartContainer } from '../ui/chart';
import { PanelStateMessage } from './PanelStateMessage';
import { formatMoney } from '../../lib/formatMoney';
import { distributeIntegerPercents } from '../../lib/sharePercents';
import { COMPACT_CHART_HEIGHT } from './panelSizes';
import { useTheme } from '../../context/ThemeContext';

function EmptyState() {
  return (
    <div className="py-6 text-center text-sm text-slate-400">
      No product mix data available
    </div>
  );
}

function SkeletonDonut({ compact = false }) {
  return (
    <div className={`flex items-center justify-center ${compact ? COMPACT_CHART_HEIGHT : 'h-[150px]'}`}>
      <div
        className={`rounded-full border-[12px] border-slate-200 animate-pulse ${
          compact ? 'h-[150px] w-[150px]' : 'h-[120px] w-[120px]'
        }`}
      />
    </div>
  );
}

export function RevenueMixPanel({ revenueByProduct, loading, error, variant = 'detailed', currencyCode = 'DKK' }) {
  const { chartPalette, primaryColor } = useTheme();
  const isCompact = variant === 'compact';
  const productLimit = isCompact ? 4 : 8;
  if (error) {
    return (
      <SectionCard title="Revenue Share by Type" description="Allocation across product categories">
        <PanelStateMessage
          variant="error"
          title="Unable to load product mix"
          message={error?.message || 'Try adjusting the filters or retrying the request.'}
        />
      </SectionCard>
    );
  }

  if (loading) {
    return (
      <SectionCard title="Revenue Share by Type" description="Allocation across product categories">
        <SkeletonDonut compact={isCompact} />
      </SectionCard>
    );
  }

  const products = (revenueByProduct || []).slice(0, productLimit);

  if (products.length === 0) {
    return (
      <SectionCard title="Revenue Share by Type" description="Allocation across product categories">
        <EmptyState />
      </SectionCard>
    );
  }

  const revenues = products.map((product) => Number(product.totalRevenue || 0));
  const shares = distributeIntegerPercents(revenues);

  const chartData = products.map((product, index) => ({
    type: product.productName,
    value: shares[index],
    revenue: revenues[index],
    fill: chartPalette[index % chartPalette.length],
  }));

  const chartConfig = chartData.reduce((acc, item) => {
    acc[item.type] = { label: item.type, color: item.fill };
    return acc;
  }, { value: { label: 'Revenue Share' } });

  const leadCategory = chartData[0];

  function RevenueMixTooltip({ active, payload }) {
    if (!active || !payload?.length) {
      return null;
    }

    const row = payload[0]?.payload;
    if (!row) {
      return null;
    }

    return (
      <div className="rounded-none border border-slate-200 bg-white px-3 py-2 text-[11px] shadow-sm">
        <div className="mb-1 font-semibold text-[#201C35]">{row.type}</div>
        <div className="space-y-0.5 text-slate-600">
          <div className="flex items-center justify-between gap-3">
            <span>Share</span>
            <span className="font-semibold text-slate-700">{row.value}%</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span>Revenue</span>
            <span className="font-semibold" style={{ color: primaryColor }}>{formatMoney(row.revenue, { currency: currencyCode })}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SectionCard title="Revenue Share by Type" description="Allocation across product categories">
      <div className={`${isCompact ? 'flex h-full flex-col space-y-2.5' : 'space-y-4'}`}>
        <div
          className={
            isCompact
              ? 'flex min-h-0 flex-1 flex-col gap-3'
              : 'grid gap-4 md:grid-cols-2 md:items-center'
          }
        >
          <ChartContainer
            id="revenue-mix-chart"
            config={chartConfig}
            className={`mx-auto w-full min-w-0 ${
              isCompact ? `${COMPACT_CHART_HEIGHT} min-h-[210px] max-w-none` : 'h-[220px] max-w-none'
            }`}
            aria-label="Revenue mix donut chart"
            tabIndex={0}
            aria-describedby="revenue-mix-announcer"
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip content={<RevenueMixTooltip />} cursor={false} />
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="type"
                  innerRadius={isCompact ? 58 : 54}
                  outerRadius={isCompact ? 88 : 78}
                  strokeWidth={6}
                  paddingAngle={2}
                >
                  {chartData.map((item) => (
                    <Cell key={item.type} fill={item.fill} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                            <tspan x={viewBox.cx} y={viewBox.cy} className="fill-slate-900 text-2xl font-bold">
                              {chartData.length && chartData.some((item) => item.value > 0) ? 100 : 0}%
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 18} className="fill-slate-500 text-[11px]">
                              Total Mix
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>

          <div
            className={
              isCompact
                ? 'space-y-1.5 text-[10px] text-slate-600'
                : 'space-y-3'
            }
          >
            {!isCompact && (
              <div
                className="rounded-xl px-4 py-3 text-white shadow-sm"
                style={{ backgroundColor: primaryColor || 'var(--brand-primary, #1A428A)' }}
              >
                <div className="text-[9px] uppercase tracking-[0.18em] text-white/75">Leading mix</div>
                <div className="mt-1 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white" title={leadCategory.type}>
                      {leadCategory.type}
                    </div>
                    <div className="text-[10px] text-white/70">Primary revenue driver</div>
                  </div>
                  <div className="text-right text-sm font-semibold text-white">{leadCategory.value}%</div>
                </div>
              </div>
            )}

              <div className="space-y-2 text-[11px] text-slate-600">
                {chartData.slice(0, isCompact ? 3 : chartData.length).map((item) => (
                  <div
                    key={item.type}
                    className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 last:border-b-0 last:pb-0"
                    tabIndex={0}
                    role="button"
                    aria-label={`${item.type}: ${item.value} percent of revenue, ${formatMoney(item.revenue, { currency: currencyCode })}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const ann = document.getElementById('revenue-mix-announcer');
                        if (ann) ann.textContent = `${item.type}, ${item.value} percent, ${formatMoney(item.revenue, { currency: currencyCode })}`;
                      }
                    }}
                    onClick={() => {
                      const ann = document.getElementById('revenue-mix-announcer');
                      if (ann) ann.textContent = `${item.type}, ${item.value} percent, ${formatMoney(item.revenue, { currency: currencyCode })}`;
                    }}
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="h-2 w-2 flex-shrink-0 rounded-sm" style={{ backgroundColor: item.fill }} />
                      <span className="truncate" title={item.type}>{item.type}</span>
                    </div>
                    <span className="font-semibold text-slate-500">{item.value}%</span>
                  </div>
                ))}
              </div>
          </div>
        </div>
        <div id="revenue-mix-announcer" className="sr-only" aria-live="polite" />
      </div>
    </SectionCard>
  );
}
